import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  food,
  foodCategory,
  userFoodEntry,
} from 'src/app/shared/models/interfaces';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { CommonModule } from '@angular/common';
import { DailyService } from 'src/app/views/logs/daily/daily.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { isSameDay } from 'src/app/shared/utils/date';

@Component({
  selector: 'app-food',
  imports: [UIComponentsModule, CommonModule, MatTooltipModule],
  templateUrl: './food.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './food.component.scss',
})
export class FoodComponent implements OnChanges {
  userFoodEntries: userFoodEntry[] = [];
  foodCategories: foodCategory[] = [];
  selectedCategory: foodCategory | null = null;
  private pendingChanges: userFoodEntry[] = [];

  @Input() selectedDate!: Date;

  // Used to navigate to next daily entry type
  @Output() continueToNext = new EventEmitter<void>();

  constructor(
    private dailyService: DailyService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    // Load initial data
    this.loadUserFoodEntries();
    this.loadFoodCategories();
  }

  ngOnChanges(): void {
    // React to changes in activityCategories input
    if (
      this.foodCategories &&
      this.foodCategories.length > 0 &&
      !this.selectedCategory
    ) {
      this.selectCategory(this.foodCategories[0]);
    }
  }

  ngOnDestroy(): void {
    if (this.pendingChanges) {
      this.syncPendingChanges();
    }
  }

  private loadUserFoodEntries() {
    // First load the user food entries in the daily service
    // and then subscribe to the observable to get real-time updates
    this.dailyService.getUserFoodEntries();
    this.dailyService.userFoodEntries$.subscribe((entries) => {
      this.userFoodEntries = entries;
    });
  }

  private loadFoodCategories() {
    // similar to user food entries
    this.dailyService.getFoodData();
    this.dailyService.foodCategories$.subscribe((categories) => {
      this.foodCategories = categories;
      this.mapFoodCategoriesToIcons(); // Map icons to categories
      // automatically select the first category and the corresponding activities if none is selected
      if (categories.length > 0 && !this.selectedCategory) {
        this.selectCategory(categories[0]);
      }
    });
  }

  private get filteredUserFoodEntries(): userFoodEntry[] {
    return this.userFoodEntries.filter((entry) =>
      isSameDay(entry.occurredAt, this.selectedDate)
    );
  }

  getAmountForFood(foodId: number): number {
    const entry = this.filteredUserFoodEntries.find((e) => e.foodId === foodId);
    return entry ? entry.nrServings : 0;
  }

  isImportedByAirplane(foodId: number): boolean {
    const entry = this.filteredUserFoodEntries.find((e) => e.foodId === foodId);
    return entry ? entry.importedByPlane || false : false;
  }

  incrementAmount(foodId: number) {
    let entry = this.filteredUserFoodEntries.find((e) => e.foodId === foodId);
    if (entry) {
      // If entry exists, increment the number of servings
      entry.nrServings++;
    } else {
      // If entry does not exist, create a new one with 1 serving
      const newEntry: userFoodEntry = {
        foodId,
        nrServings: 1,
        importedByPlane: false,
        occurredAt: this.selectedDate,
      };
      this.userFoodEntries.push(newEntry);
    }
    this.trackPendingChange(foodId);
  }

  decrementAmount(foodId: number) {
    const entry = this.filteredUserFoodEntries.find((e) => e.foodId === foodId);
    if (entry) {
      if (entry.nrServings > 0) {
        entry.nrServings--;
        this.trackPendingChange(foodId);
      }
    }
  }

  toggleImportedByAirplane(foodId: number, event: Event) {
    // Sets the importedByPlane property based on the checkbox state
    // Different calculation is done in the backend based on this property
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    const entry = this.filteredUserFoodEntries.find((e) => e.foodId === foodId);
    if (entry) {
      entry.importedByPlane = isChecked;
      this.trackPendingChange(foodId);
    } else {
      this.snackbarService.showErrorMessage(
        'Please add at least one serving before marking as imported.'
      );
      checkbox.checked = false; // Revert the checkbox state
    }
  }

  private trackPendingChange(foodId: number) {
    // Track changes to the food entry for syncing later
    // This method is called whenever an entry is modified
    const entry = this.filteredUserFoodEntries.find((e) => e.foodId === foodId);
    if (entry) {
      const existingChangeIndex = this.pendingChanges.findIndex(
        (e) => e.foodId === foodId
      );
      if (existingChangeIndex !== -1) {
        this.pendingChanges[existingChangeIndex] = { ...entry };
      } else {
        this.pendingChanges.push({ ...entry });
      }
    }
  }

  selectCategory(category: foodCategory) {
    // Sync changes before switching categories
    this.syncPendingChanges();
    this.selectedCategory = category;
  }

  getNextCategoryIcon(category: foodCategory): string {
    // Returns the icon for the next category based on the current category's ID.
    // Displaying an arrow if there is no next category
    const currentCategoryId = category.id;
    const nextIcon = this.foodCategories.find(
      (cat) => cat.id === currentCategoryId + 1
    )?.icon;
    if (nextIcon) {
      return nextIcon;
    }
    return 'arrow_right';
  }

  hasEntries(category: foodCategory): boolean {
    // Check if there are any user food entries for the given category
    return (
      category.foods?.some((food) =>
        this.filteredUserFoodEntries.some(
          (entry) => entry.foodId === food.id && entry.nrServings > 0
        )
      ) || false
    );
  }

  onContinue() {
    // Sync changes before continuing to the next category
    this.syncPendingChanges();
    const currentIndex = this.foodCategories.findIndex(
      (category) => category.id === this.selectedCategory?.id
    );
    if (
      this.selectedCategory &&
      currentIndex < this.foodCategories.length - 1
    ) {
      this.selectCategory(this.foodCategories[currentIndex + 1]);
    } else {
      // Last category, emit event to parent to switch to next tab
      this.continueToNext.emit();
    }
  }

  private syncPendingChanges() {
    const changesToSync = [...this.pendingChanges];
    this.pendingChanges = [];

    changesToSync.forEach((entry) => {
      if (entry.nrServings > 0) {
        if (entry.id) {
          // Update existing entry - service will handle state update
          this.dailyService.updateUserFoodEntry(entry).subscribe({
            error: (err) => {
              console.error('Error updating food entry:', err);
              this.snackbarService.showErrorMessage(
                'Failed to update food entry. Please try again.'
              );
            },
          });
        } else {
          // Create new entry - service will handle state update
          this.dailyService.createUserFoodEntry(entry).subscribe({
            next: (createdEntry) => {
              const idx = this.userFoodEntries.findIndex(
                (e) => e.foodId === entry.foodId
              );
              if (idx !== -1) {
                this.userFoodEntries[idx] = createdEntry;
              }
            },
            error: (err) => {
              console.error('Error creating food entry:', err);
              this.snackbarService.showErrorMessage(
                'Failed to create food entry. Please try again.'
              );
            },
          });
        }
      } else if (entry.id) {
        // Delete entry if servings are 0 - service will handle state update
        this.dailyService.deleteUserFoodEntry(entry.id).subscribe({
          error: (err) => {
            console.error('Error deleting food entry:', err);
            this.snackbarService.showErrorMessage(
              'Failed to delete food entry. Please try again.'
            );
          },
        });
      }
    });
  }

  private mapFoodCategoriesToIcons() {
    this.foodCategories = this.foodCategories.map((category) => {
      const categoryKey = category.text.text_en || category.text.text_de;
      return {
        ...category,
        icon: foodIcons[categoryKey] || '',
      };
    });
  }
}

// Icons for food and activity categories
const foodIcons: { [key: string]: string } = {
  'milk, dairy products & plant-based milk alternatives': 'milk',
  'meat, fish & eggs': 'meat',
  'fruit & vegetables': 'apple',
  'meat substitutes & pulses': 'leaf',
  'cereal products & potatoes': 'wheat',
  'drinks': 'coffee',
  'nuts & seeds': 'nut',
  'snacks (sweet & savoury)': 'cookie',
  'spread': 'honey',
};
